import { isAxiosError } from 'axios';
import { isPlainObject } from 'es-toolkit';
import { cartClient } from '../common/cartClient';
import {
  SkinAnalysisActiveResultError,
  SkinAnalysisHautRunUnavailableError,
  SkinAnalysisImageQualityError,
  SkinAnalysisRecommendationProductGoneError,
  SkinAnalysisRecommendationProductsUnavailableError,
  SkinAnalysisRecommendationsNotReadyError,
  SkinAnalysisReportLimitError,
  SkinAnalysisSurveyRequiredError,
  isSkinAnalysisInFlightStatus,
  isSkinAnalysisResultSectionStatus,
} from '../../model/skinAnalysis/skinAnalysis';
import type {
  CreateSkinAnalysisHautRunRequest,
  CreateSkinAnalysisHautRunResponse,
  CreateSkinAnalysisImageUploadUrlRequest,
  CreateSkinAnalysisImageUploadUrlResponse,
  CreateSkinAnalysisResultQualityFailure,
  CreateSkinAnalysisResultRequest,
  CreateSkinAnalysisResultResponse,
  GetActiveSkinAnalysisSurveyRequest,
  GetActiveSkinAnalysisSurveyResponse,
  GetMySkinAnalysisOverviewResponse,
  GetMySkinAnalysisStatusResponse,
  GetMySkinAnalysisSurveyResponse,
  GetSkinAnalysisEnvironmentResponse,
  GetSkinAnalysisLocationResponse,
  GetSkinAnalysisRecommendationProductDetailRequest,
  GetSkinAnalysisRecommendationProductDetailResponse,
  GetSkinAnalysisRecommendationProductsRequest,
  GetSkinAnalysisRecommendationProductsResponse,
  GetSkinAnalysisRecommendationsPathParams,
  GetSkinAnalysisRecommendationsResponse,
  GetSkinAnalysisReportPathParams,
  GetSkinAnalysisReportResponse,
  GetSkinAnalysisResultStatusPathParams,
  GetSkinAnalysisResultStatusResponse,
  PutSkinAnalysisLocationRequest,
  PutSkinAnalysisLocationResponse,
  SearchSkinAnalysisLocationsRequest,
  SearchSkinAnalysisLocationsResponse,
  SubmitSkinAnalysisSurveyResponseRequest,
  SubmitSkinAnalysisSurveyResponseResponse,
} from './utils/types';

/** Narrow an unknown body to the `POST /results` quality-failure shape (contract API 6). */
function isImageQualityFailure(data: unknown): data is CreateSkinAnalysisResultQualityFailure {
  return isPlainObject(data) && data.code === 'image_quality_failed' && Array.isArray(data.qualityIssues);
}

function messageOf(body: Record<string, unknown>, fallback: string): string {
  return typeof body.message === 'string' && body.message.trim().length > 0 ? body.message : fallback;
}

function countOf(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/**
 * Map a `POST /results` rejection onto the typed error for its `code`, or null when the body
 * carries none of the codes the app can act on.
 *
 * Every one of these is a DIFFERENT next step — resume the running analysis, go back to the
 * questionnaire, explain the report limit, retake the photos — so they cannot share the generic
 * "we could not start your analysis" path the screen used to funnel them all into.
 */
/**
 * Map a `GET /recommendations` 409 onto its typed error, or null for anything else — including a
 * 409 the app does not recognise, which stays a plain rejection rather than being read as a
 * verdict the body never gave.
 */
function recommendationsNotReadyErrorFrom(error: unknown): Error | null {
  if (!isAxiosError(error) || error.response?.status !== 409) {
    return null;
  }

  const body = error.response.data;
  if (!isPlainObject(body) || body.code !== 'result_not_ready') {
    return null;
  }

  return new SkinAnalysisRecommendationsNotReadyError(
    messageOf(body, 'Your routine is not ready yet.'),
    isSkinAnalysisResultSectionStatus(body.status) ? body.status : null,
  );
}

function createResultErrorFrom(error: unknown): Error | null {
  const body = isAxiosError(error) ? error.response?.data : null;
  if (!isPlainObject(body) || typeof body.code !== 'string') {
    return null;
  }

  switch (body.code) {
    case 'image_quality_failed':
      return new SkinAnalysisImageQualityError(
        messageOf(body, 'Please retake all three photos.'),
        Array.isArray(body.qualityIssues) ? body.qualityIssues : [],
      );
    case 'active_result_exists':
      return new SkinAnalysisActiveResultError(
        messageOf(body, 'A Skin Analysis result is already in progress.'),
        typeof body.resultId === 'string' ? body.resultId : null,
        isSkinAnalysisInFlightStatus(body.status) ? body.status : null,
      );
    case 'report_limit_reached':
      return new SkinAnalysisReportLimitError(
        messageOf(body, 'You have used all of your Skin Analysis reports.'),
        countOf(body.completedReportCount),
        countOf(body.maxCompletedReportCount),
      );
    case 'survey_required':
      return new SkinAnalysisSurveyRequiredError(messageOf(body, 'Please complete the skin survey before scanning.'));
    case 'haut_run_unavailable':
    case 'haut_run_expired':
    case 'haut_run_already_bound':
      return new SkinAnalysisHautRunUnavailableError(messageOf(body, 'Your scan is unavailable.'), body.code);
    default:
      return null;
  }
}

/**
 * Skin Analysis P1 app-facing client (Notion contract, 2026-06-29).
 *
 * Uses `cartClient`, NOT the Facade `client`: `/skinAnalysis/*` auth needs the
 * WordPress session cookie, which is scoped to the WordPress domain. The Facade
 * `client` targets the `api.*` origin — cross-domain, so the WP cookie is NOT sent.
 * `cartClient` targets the WordPress `{origin}/api` proxy (same domain as login), so
 * the cookie rides along, exactly like cart. Routes therefore resolve to
 * `{WP}/api/skinAnalysis/*`.
 */
class SkinAnalysisAPIClient {
  // ----- API 1: me/status -----
  public async getMyStatus(): Promise<GetMySkinAnalysisStatusResponse> {
    return await cartClient.get('/skinAnalysis/me/status').then((res) => res.data);
  }

  // ----- me/overview: everything the Skin Diary entry page renders, in one call -----
  public async getMyOverview(): Promise<GetMySkinAnalysisOverviewResponse> {
    return await cartClient.get('/skinAnalysis/me/overview').then((res) => res.data);
  }

  // ----- API 2: survey/active -----
  /**
   * `signal` lets React Query abandon the request when the query key changes or the
   * questionnaire unmounts, so a stalled response cannot hold the survey screen on its
   * suspense fallback for good (SSD-6058).
   */
  public async getActiveSurvey(
    params: GetActiveSkinAnalysisSurveyRequest = {},
    signal?: AbortSignal,
  ): Promise<GetActiveSkinAnalysisSurveyResponse> {
    return await cartClient.get('/skinAnalysis/survey/active', { params, signal }).then((res) => res.data);
  }

  /**
   * The caller's own latest survey submission, as a read-only question-and-answer view.
   * Answers are rendered against the version they were captured under, so a later question
   * change cannot retitle or reorder an old submission.
   */
  public async getMySurvey(): Promise<GetMySkinAnalysisSurveyResponse> {
    return await cartClient.get('/skinAnalysis/me/survey').then((res) => res.data);
  }

  // ----- API 3: survey/responses -----
  public async submitSurveyResponse(
    payload: SubmitSkinAnalysisSurveyResponseRequest,
  ): Promise<SubmitSkinAnalysisSurveyResponseResponse> {
    return await cartClient.post('/skinAnalysis/survey/responses', payload).then((res) => res.data);
  }

  // ----- API 4: location preference -----
  public async getLocation(): Promise<GetSkinAnalysisLocationResponse> {
    return await cartClient.get('/skinAnalysis/me/location').then((res) => res.data);
  }

  public async putLocation(payload: PutSkinAnalysisLocationRequest): Promise<PutSkinAnalysisLocationResponse> {
    const data: PutSkinAnalysisLocationResponse = await cartClient
      .put('/skinAnalysis/me/location', payload)
      .then((res) => res.data);
    if (data.persisted !== true) {
      throw new Error('Your location could not be saved. Please try again.');
    }
    return data;
  }

  // ----- live environment for the current location -----

  /**
   * 503 `environment_unavailable` when nothing is measurable, 409 when no location resolves.
   */
  public async getEnvironment(): Promise<GetSkinAnalysisEnvironmentResponse> {
    return await cartClient.get('/skinAnalysis/me/environment').then((res) => res.data);
  }

  // ----- locations/search: worldwide city autocomplete (Open-Meteo, server-side) -----
  public async searchLocations(
    params: SearchSkinAnalysisLocationsRequest,
  ): Promise<SearchSkinAnalysisLocationsResponse> {
    return await cartClient.get('/skinAnalysis/locations/search', { params }).then((res) => res.data);
  }

  // ----- API 5: image-upload-url -----
  public async createImageUploadUrl(
    payload: CreateSkinAnalysisImageUploadUrlRequest,
  ): Promise<CreateSkinAnalysisImageUploadUrlResponse> {
    return await cartClient.post('/skinAnalysis/image-upload-url', payload).then((res) => res.data);
  }

  // ----- hautRuns: survey-independent image run (SSD-6097) -----

  /**
   * Starts analysing the photos before any survey exists for them. The same `code`s as
   * `POST /results` come back typed — a running analysis is the one the app can meet here.
   */
  public async createHautRun(payload: CreateSkinAnalysisHautRunRequest): Promise<CreateSkinAnalysisHautRunResponse> {
    return await cartClient
      .post('/skinAnalysis/hautRuns', payload)
      .then((res) => res.data)
      .catch((error: unknown) => {
        const typed = createResultErrorFrom(error);
        if (typed != null) {
          throw typed;
        }
        throw error;
      });
  }

  // ----- API 6: results -----
  public async createResult(payload: CreateSkinAnalysisResultRequest): Promise<CreateSkinAnalysisResultResponse> {
    // The rejections that matter are synchronous and carry a `code`: quality failure (400),
    // and the 409s — an analysis already running, the report limit, a missing survey. Each is
    // raised as its own error type so the pipeline can route instead of apologising.
    // A 200 body carrying `image_quality_failed` is tolerated too (see below).
    const data = await cartClient
      .post('/skinAnalysis/results', payload)
      .then((res) => res.data)
      .catch((error: unknown) => {
        const typed = createResultErrorFrom(error);
        if (typed != null) {
          throw typed;
        }
        throw error;
      });

    if (isImageQualityFailure(data)) {
      throw new SkinAnalysisImageQualityError(data.message, data.qualityIssues);
    }
    return data as CreateSkinAnalysisResultResponse;
  }

  // ----- API 7: status -----
  public async getResultStatus(
    params: GetSkinAnalysisResultStatusPathParams,
  ): Promise<GetSkinAnalysisResultStatusResponse> {
    return await cartClient.get(`/skinAnalysis/results/${params.resultId}/status`).then((res) => res.data);
  }

  // ----- API 8: report -----
  public async getReport(params: GetSkinAnalysisReportPathParams): Promise<GetSkinAnalysisReportResponse> {
    return await cartClient.get(`/skinAnalysis/results/${params.resultId}/report`).then((res) => res.data);
  }

  // ----- API 9: recommendations -----
  public async getRecommendations(
    params: GetSkinAnalysisRecommendationsPathParams,
  ): Promise<GetSkinAnalysisRecommendationsResponse> {
    // The 409 is raised as its own type because its body says whether the routine is still
    // coming or will never arrive, and the screen owes the reader different copy for each.
    return await cartClient
      .get(`/skinAnalysis/results/${params.resultId}/recommendations`)
      .then((res) => res.data)
      .catch((error: unknown) => {
        const typed = recommendationsNotReadyErrorFrom(error);
        if (typed != null) {
          throw typed;
        }
        throw error;
      });
  }

  // ----- Live recommendation products (SSD-6340) -----

  /**
   * The step-by-step list of currently purchasable occurrences, re-priced in `currency`.
   * Shares the recommendations' 409 (the result is still being built) and adds a typed 503.
   */
  public async getRecommendationProducts({
    resultId,
    currency,
  }: GetSkinAnalysisRecommendationProductsRequest): Promise<GetSkinAnalysisRecommendationProductsResponse> {
    return await cartClient
      .get(`/skinAnalysis/results/${resultId}/recommendationProducts`, { params: { currency } })
      .then((res) => res.data)
      .catch((error: unknown) => {
        throw recommendationProductsErrorFrom(error) ?? error;
      });
  }

  /**
   * One occurrence in full. A 404 here is typed: it means the occurrence stopped being
   * purchasable since the list was read, which the sheet has to say rather than fail on.
   */
  public async getRecommendationProductDetail({
    resultId,
    recommendationItemId,
    currency,
  }: GetSkinAnalysisRecommendationProductDetailRequest): Promise<GetSkinAnalysisRecommendationProductDetailResponse> {
    return await cartClient
      .get(`/skinAnalysis/results/${resultId}/recommendationProducts/${recommendationItemId}`, { params: { currency } })
      .then((res) => res.data)
      .catch((error: unknown) => {
        if (isAxiosError(error) && error.response?.status === 404) {
          const body = error.response.data;
          throw new SkinAnalysisRecommendationProductGoneError(
            isPlainObject(body) ? messageOf(body, 'This product is no longer available.') : 'This product is no longer available.',
          );
        }
        throw recommendationProductsErrorFrom(error) ?? error;
      });
  }
}

/**
 * The failures the live product reads share: the recommendations' own 409 (`result_not_ready`),
 * and their 503, which is raised as its own type because the contract forbids reading it as an
 * empty or sold-out list. Anything else stays the plain rejection it was.
 */
function recommendationProductsErrorFrom(error: unknown): Error | null {
  const notReady = recommendationsNotReadyErrorFrom(error);
  if (notReady != null) return notReady;

  if (!isAxiosError(error) || error.response?.status !== 503) return null;

  const body = error.response.data;
  return new SkinAnalysisRecommendationProductsUnavailableError(
    isPlainObject(body)
      ? messageOf(body, 'Products are temporarily unavailable.')
      : 'Products are temporarily unavailable.',
  );
}

export const skinAnalysisAPIClient = new SkinAnalysisAPIClient();
